import React, { useState } from 'react'
import { Check, X } from 'lucide-react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@/components/ui/button'
import { Form, FormControl, FormField, FormItem, FormMessage, FormLabel } from '@/components/ui/form'
import { CustomDialog } from '@/components/dialogs/custom-dialog'
import { useAuthStore } from '@/store/auth'
import { showConfirm } from '@/lib/alerts'
import { api } from '@/lib/axios'
import { useToast } from '@/hooks/use-toast'
import { HTTP_STATUS_CODE } from '@/constants/http'
import { useLoader } from '@/contexts/LoaderContext'
import { Input } from '@/components/ui/input'
import { Select, SelectTrigger, SelectContent, SelectItem } from '@/components/ui/select'
import { createAnamneseSchema, CreateAnamneseSchema, AnamneseProps, formatted_anamnese_profile_types } from '@/entities/anamnese'
import { FileUpload } from '@/components/uploaders/file-upload'
import { formatted_exam_library_profile_types } from '@/entities/exam-library'
import { uploadFile } from '@/utils/upload-util'
import { UserProps } from '@/entities/user'

// types
type CreateAnamneseDialogProps = {
  anamneseProp: AnamneseProps
  open: boolean
  cb: () => void
}

// variables
const loc = 'components/dialogs/create-exam-library-dialog'

export function CreateAnamneseDialog({ anamneseProp, open, cb }: CreateAnamneseDialogProps) {
  const { user } = useAuthStore()
  const { toast } = useToast()
  const { showLoader, hideLoader, isLoading } = useLoader()
  const [isOpen, setIsOpen] = useState<boolean>(open)
  const [file, setFile] = useState<{
    file: File | null
    name: string | null
    base64: string | null
    extension: string | null
    url: string | null
  }>({
    file: null,
    name: null,
    base64: null,
    extension: null,
    url: null
  })

  const form = useForm<CreateAnamneseSchema>({
    resolver: zodResolver(createAnamneseSchema),
    defaultValues: {
      ...anamneseProp,
      userRef: user?.ref
    }
  })

  const formValues = useWatch({ control: form.control })

  const _handleCreate = async () => {
    try {
      const isValid = await form.trigger()
      if (!isValid) return

      const { isConfirmed } = await showConfirm('Deseja confirmar?')
      if (!isConfirmed) return

      showLoader()

      const urls: string[] = []

      if (file?.file) {
        const url = await uploadFile(file?.file, user as UserProps, 'anamnese')
        if (url) urls.push(url)
        form.setValue('paths', urls)
        _clearFile()
      }

      const response = anamneseProp?.ref
        ? await api.put('/', {
            ...formValues,
            action: 'anamnese',
            userRef: user?.ref,
            ref: anamneseProp.ref,
            paths: urls
          })
        : await api.post('/', {
            ...formValues,
            action: 'anamnese',
            userRef: user?.ref,
            paths: urls
          })

      if ([HTTP_STATUS_CODE.CREATED, HTTP_STATUS_CODE.OK].includes(response.data?.statusCode)) {
        toast({
          variant: 'success',
          title: 'Sucesso',
          description: response.data?.statusMessage || `O modelo de anamnese foi ${anamneseProp.ref ? 'atualizado' : 'criado'} com sucesso.`
        })
        cb()
      } else toast({ variant: 'destructive', title: 'Ops ...', description: response.data?.statusMessage || 'Não foi possível criar o modelo de anamnese.' })
    } catch (err) {
      toast({ variant: 'destructive', title: 'Ops ...', description: 'Não foi possível criar o modelo de anamnese.' })
      console.error(`Unhandled error at ${loc}._handleCreate. Details: ${err}`)
    } finally {
      hideLoader()
    }
  }

  const _handleCancel = async () => {
    const { isConfirmed } = await showConfirm('Deseja cancelar?')
    if (!isConfirmed) return

    setIsOpen(false)
    cb()
  }

  const _handleOnUpload = (receivedFiles: File[]) => {
    receivedFiles = receivedFiles.filter(rf => rf instanceof File)

    if (!receivedFiles?.length) {
      _clearFile()
      return
    }

    receivedFiles.forEach(uploadedFile => {
      const ext = uploadedFile?.name?.split('.').pop() || ''
      const reader = new FileReader()
      reader.onload = () => {
        const base64String = reader.result as string
        _handleFileUpload(uploadedFile, base64String, ext)
      }
      reader.readAsDataURL(uploadedFile)
    })
  }

  const _clearFile = () => {
    setFile({
      file: null,
      name: null,
      base64: null,
      extension: null,
      url: null
    })
  }

  const _handleFileUpload = (file: File, base64: string, extension: string) => {
    setFile({
      file: file,
      name: file.name,
      base64,
      extension,
      url: null
    })
  }

  return (
    <CustomDialog
      open={isOpen}
      persistent={true}
      title={`${anamneseProp?.ref ? 'Atualizar' : 'Criar'} modelo de anamnese`}
      confirmationBeforeClose={isOpen}
      onClose={() => {
        setIsOpen(false)
        cb()
      }}
    >
      <Form {...form}>
        <form
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault()
              _handleCreate()
            }
          }}
        >
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem className="w-full mb-5">
                <FormLabel>Título: *</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Digite o título" disabled={isLoading} />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="profileType"
            render={field => (
              <FormItem className="w-auto mb-5">
                <FormLabel>Perfil: *</FormLabel>
                <FormControl>
                  <Select {...field} value={field?.value?.toString() || ''} onValueChange={value => form.setValue('profileType', value as exam_library_profile_types)}>
                    <SelectTrigger className="flex-1 rounded-lg truncate">
                      <span>{formatted_anamnese_profile_types[form.getValues('profileType')]}</span>
                    </SelectTrigger>
                    <SelectContent className="border-border truncate rounded-lg">
                      {Object.entries(formatted_exam_library_profile_types).map(([value, text]) => (
                        <SelectItem key={value} value={value} className="rounded-lg text-sm truncate hover:cursor-pointer">
                          {text}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />

          <FileUpload
            onUpload={_handleOnUpload}
            onClear={_clearFile}
            multiple={false}
            maxFiles={1}
            hideOnUpload={true}
            accept={{
              'image/*': ['.png', '.jpg', '.jpeg'],
              'application/*': ['.pdf'],
              'audio/*': ['.mp3', '.ogg'],
              'video/*': ['.mp4']
            }}
          />

          <div className="col-span-2 mt-4 pt-4 border-t flex items-center gap-x-2 justify-end">
            <Button variant="default" type="button" className="flex gap-x-2" onClick={_handleCreate}>
              {anamneseProp?.ref ? 'Atualizar' : 'Criar'}
              <Check className="size-5" />
            </Button>

            <Button variant="outline" type="button" className="flex gap-x-2 border-none" onClick={_handleCancel}>
              Cancelar
              <X className="size-4" />
            </Button>
          </div>
        </form>
      </Form>
    </CustomDialog>
  )
}
